import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SectionTitle, ClaimNote } from "@/components/Wave";
import { LazyVideo } from "@/components/LazyVideo";
import { HowGroup, StackFeature, OceanCard, useStacks } from "@/components/Stacks";
import { MEDIA, HOW_GROUPS, STORY, STACKS, NOT_STUDIED } from "@/data/stacks";
import { FDA } from "@/data/content";
import { MEET_FINN } from "@/data/ocean";
import { UsdOnlyNote } from "@/components/Usd";
import { POSTS } from "@/data/blog";

const live = (keys) => keys.filter((k) => MEDIA[k]?.src);

const Reels = ({ keys, prefix, light = false }) => (
  <>
    <div className="mt-10 flex gap-6 overflow-x-auto snap-x pb-4 -mx-4 px-4">
      {live(keys).map((k) => <LazyVideo key={k} media={MEDIA[k]} light={light} caption={false} testId={`${prefix}-${k}`} className="snap-start shrink-0 w-[70vw] sm:w-72" />)}
    </div>
    <p data-testid={`${prefix}-fda`} className={`mt-2 text-[11px] leading-snug ${light ? "text-sand/80" : "text-slate-600"}`}>*{FDA}</p>
  </>
);

export const Advice = () => !live(["ep1Storm", "ep1Cartoon", "ep2"]).length ? null : (
  <section id="advice" data-testid="advice-section" className="bg-navy text-sand">
    <div className="container-gf section">
      <SectionTitle light chip="Grandpa's Advice" title="Grandpa's Advice" sub="Short stories from the jetty. Pull up a crate." />
      <Reels keys={["ep1Storm", "ep1Cartoon", "ep2"]} prefix="advice" light />
    </div>
  </section>
);

export const ShopStacks = () => {
  const stacks = useStacks();
  if (!stacks.length) return null;
  const reels = stacks.filter((p) => !STACKS[p.id].ocean);
  const ocean = stacks.filter((p) => STACKS[p.id].ocean);
  return (
    <section id="stacks" data-testid="stacks-section" className="container-gf section">
      <SectionTitle chip="The stacks" title="Shop the stacks" sub="Bundles that keep the routine simple. Check each stack for where it ships." />
      <div className="mt-12 space-y-16">{reels.map((p, i) => <StackFeature key={p.id} p={p} i={i} />)}</div>
      {ocean.length > 0 && (
        <div data-testid="ocean-range-row" className="mt-20">
          <h3 className="text-2xl md:text-4xl">The Ocean range</h3>
          <p className="mt-2 text-slate-600">Ships to the US, Canada, Europe, Australia & NZ.</p>
          <div className="mt-8 grid gap-6 md:grid-cols-3">{ocean.map((p) => <OceanCard key={p.id} p={p} />)}</div>
        </div>
      )}
      <ClaimNote testId="stacks-claim-disclaimer" />
      <UsdOnlyNote className="mt-3" testId="stacks-usd-note" />
    </section>
  );
};

export const MeetFinn = () => (
  <section data-testid="meet-finn-section" className="bg-navy text-sand">
    <div className="container-gf section max-w-3xl">
      <span className="label-chip">Meet Grandpa Finn</span>
      <p data-testid="meet-finn-text" className="mt-6 text-lg leading-relaxed text-sand/90">{MEET_FINN}</p>
    </div>
  </section>
);

export const HowItWorks = () => (
  <section id="how-it-works" data-testid="how-it-works-section" className="bg-foam">
    <div className="container-gf section space-y-12">
      <SectionTitle chip="How it works" title="The ingredient science" sub="What the research says about the ingredients, with sources." />
      {HOW_GROUPS.map((g) => <HowGroup key={g.id} g={g} fda={false} />)}
      <p data-testid="how-fda" className="text-xs text-slate-600">*{FDA} {NOT_STUDIED}</p>
    </div>
  </section>
);

export const ScienceReels = () => !live([]).length ? null : (
  <section data-testid="science-reels-section" className="container-gf section">
    <SectionTitle chip="Science reels" title="Science breakdowns" sub="Grandpa grabs the magnifying glass." />
    <Reels keys={[]} prefix="science-reel" />
  </section>
);

export const LaunchAds = () => !live(["adOmega", "adStack"]).length ? null : (
  <section data-testid="launch-ads-section" className="bg-white/50">
    <div className="container-gf section">
      <SectionTitle chip="Now playing" title="Grandpa on your FYP" sub="Short, salty, slightly unhinged." />
      <Reels keys={["adOmega", "adStack"]} prefix="launch-ad" />
    </div>
  </section>
);

export const BlogTeaser = () => (
  <section data-testid="blog-teaser-section" className="container-gf section">
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
      <SectionTitle chip="From the blog" title="Grandpa's reading list" />
      <Link to="/blog" data-testid="blog-teaser-all" className="btn-pop bg-white self-start">All posts <ArrowRight className="h-4 w-4" /></Link>
    </div>
    <div className="mt-10 grid gap-6 md:grid-cols-2">
      {POSTS.slice(0, 2).map((p) => (
        <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`blog-teaser-${p.slug}`} className="group sticker bg-white p-6 md:p-8 transition-transform hover:-translate-y-1">
          <h3 className="text-xl md:text-2xl leading-snug">{p.title}</h3>
          <p className="mt-2 text-slate-600 line-clamp-1">{p.description}</p>
          <span className="mt-4 inline-flex items-center gap-2 font-semibold underline decoration-sunny decoration-2 underline-offset-4">Read <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
        </Link>
      ))}
    </div>
  </section>
);

export const Story = () => (
  <section data-testid="story-section" className="bg-sand">
    <div className="container-gf section max-w-3xl">
      <span className="label-chip">Grandpa's story</span>
      <p data-testid="story-text" className="mt-6 font-display text-2xl md:text-3xl leading-snug">{STORY}</p>
    </div>
  </section>
);
